import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Stack,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useDeploy } from '../contexts/DeployContext';

const PRESETS = ['hyperliquid', 'polymarket', 'dex', 'kalshi'];

const TEMPLATE = `mbd.ingest('my_overlay')
  .preset('hyperliquid')
  .tune('convergence', { p0_threshold: 8 })
  .disable('whale_move')
  .signal('top_trader_converge', {
    detector: 'convergence',
    p0_threshold: 3,
    wallet_filter: ['0xabc', '0xdef'],
  })
  .writeTo('alpha')
  .execute();`;

// Lightweight regex-based parser of the DSL source. Avoids actually
// evaluating customer JS. Same pattern algo-runner's filterExtractor
// uses for .index() and .filter() — conservative, anything weird is
// dropped silently and the server-side validator catches misuse.
function parseDslSource(src) {
  if (typeof src !== 'string') return null;
  const ingestMatch = src.match(/mbd\.ingest\(\s*['"]([^'"]+)['"]\s*\)/);
  if (!ingestMatch) return { error: 'missing mbd.ingest(\'name\')' };
  const name = ingestMatch[1];

  const presetMatch = src.match(/\.preset\(\s*['"]([^'"]+)['"]\s*\)/);
  if (!presetMatch) return { error: 'missing .preset(\'…\')' };
  const preset = presetMatch[1];

  const tune = {};
  const tuneRe = /\.tune\(\s*['"]([^'"]+)['"]\s*,\s*(\{[\s\S]*?\})\s*\)/g;
  let m;
  while ((m = tuneRe.exec(src)) !== null) {
    try {
      // safe-ish parse: only allow object literals with simple values.
      // eslint-disable-next-line no-new-func
      const kwargs = new Function(`return (${m[2]})`)();
      if (kwargs && typeof kwargs === 'object') tune[m[1]] = kwargs;
    } catch {
      return { error: `unparseable .tune('${m[1]}', {...}) — keep kwargs as a plain object literal` };
    }
  }

  const disable = [];
  const disableRe = /\.disable\(\s*['"]([^'"]+)['"]\s*\)/g;
  while ((m = disableRe.exec(src)) !== null) disable.push(m[1]);

  const custom_signals = {};
  const signalRe = /\.signal\(\s*['"]([^'"]+)['"]\s*,\s*(\{[\s\S]*?\})\s*\)/g;
  while ((m = signalRe.exec(src)) !== null) {
    try {
      // eslint-disable-next-line no-new-func
      const kwargs = new Function(`return (${m[2]})`)();
      if (kwargs && typeof kwargs === 'object' && typeof kwargs.detector === 'string') {
        custom_signals[m[1]] = kwargs;
      } else {
        return { error: `.signal('${m[1]}', {...}) must include a detector: string` };
      }
    } catch {
      return { error: `unparseable .signal('${m[1]}', {...})` };
    }
  }

  let write_to = 'alpha';
  const writeMatch = src.match(/\.writeTo\(\s*['"]([^'"]+)['"]\s*\)/);
  if (writeMatch) {
    if (!['alpha', 'customer'].includes(writeMatch[1])) {
      return { error: '.writeTo() must be \'alpha\' or \'customer\'' };
    }
    write_to = writeMatch[1];
  }

  return {
    name,
    spec: { preset, tune, disable, custom_signals, write_to },
  };
}

function IngestForm({ editing, onSaved, onClear }) {
  const { deployApi } = useDeploy();
  const [source, setSource] = useState(editing?.algorithm || TEMPLATE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (editing) setSource(editing.algorithm || rebuildDslFromSpec(editing));
    else setSource(TEMPLATE);
    setError(null);
    setSuccess(null);
  }, [editing]);

  const parsed = useMemo(() => parseDslSource(source), [source]);

  const handleSave = useCallback(async () => {
    setError(null);
    setSuccess(null);
    if (!parsed || parsed.error) {
      setError(parsed?.error || 'unparseable DSL');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: parsed.name,
        algorithm: source,
        spec: parsed.spec,
      };
      let result;
      if (editing?.overlay_id) {
        result = await deployApi.ingests.update(editing.overlay_id, payload);
      } else {
        result = await deployApi.ingests.create(payload);
      }
      setSuccess(editing?.overlay_id ? 'Updated' : 'Created');
      onSaved?.(result);
    } catch (err) {
      setError(err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [parsed, source, editing, deployApi, onSaved]);

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 600 }}>
          {editing?.overlay_id ? `Edit overlay #${editing.overlay_id}` : 'New ingest overlay'}
        </Typography>
        {editing?.overlay_id && (
          <Button size="small" onClick={onClear}>Clear</Button>
        )}
      </Box>

      <Stack spacing={1.5}>
        <Box>
          <Typography variant="caption" color="text.secondary">Preset (alpha source key)</Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap">
            {PRESETS.map((p) => (
              <Chip
                key={p}
                label={p}
                size="small"
                color={parsed?.spec?.preset === p ? 'primary' : 'default'}
                variant={parsed?.spec?.preset === p ? 'filled' : 'outlined'}
              />
            ))}
          </Stack>
        </Box>

        <TextField
          label="DSL source"
          multiline
          minRows={14}
          maxRows={28}
          value={source}
          onChange={(e) => setSource(e.target.value)}
          fullWidth
          InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
          spellCheck={false}
        />

        {parsed && !parsed.error && (
          <Alert severity="success" variant="outlined">
            Parsed: <strong>{parsed.name}</strong> · preset <code>{parsed.spec.preset}</code>
            {' '}· tune:{Object.keys(parsed.spec.tune || {}).length}
            {' '}· disable:{(parsed.spec.disable || []).length}
            {' '}· custom:{Object.keys(parsed.spec.custom_signals || {}).length}
            {' '}· writeTo:<code>{parsed.spec.write_to}</code>
          </Alert>
        )}
        {parsed?.error && <Alert severity="warning">{parsed.error}</Alert>}
        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>}

        <Divider />

        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            disabled={saving || !parsed || parsed.error}
            onClick={handleSave}
          >
            {editing?.overlay_id ? 'Update' : 'Save'}
          </Button>
        </Stack>

        <Alert severity="info" variant="outlined" sx={{ '& .MuiAlert-message': { fontSize: 12 } }}>
          New overlays save with <code>enabled=false</code>. An admin must flip <code>enabled=true</code> on the row before alpha applies it. Until then the overlay is dormant.
        </Alert>
      </Stack>
    </Paper>
  );
}

// Reconstruct a DSL snippet from a stored spec so the editor can show
// the user something readable when they click on an existing overlay
// that was created via API without saving the raw algorithm string.
function rebuildDslFromSpec(row) {
  if (!row?.spec) return TEMPLATE;
  const { spec } = row;
  const lines = [`mbd.ingest('${row.name}')`];
  lines.push(`  .preset('${spec.preset}')`);
  for (const [k, v] of Object.entries(spec.tune || {})) {
    lines.push(`  .tune('${k}', ${JSON.stringify(v)})`);
  }
  for (const d of spec.disable || []) {
    lines.push(`  .disable('${d}')`);
  }
  for (const [k, v] of Object.entries(spec.custom_signals || {})) {
    lines.push(`  .signal('${k}', ${JSON.stringify(v)})`);
  }
  if (spec.write_to && spec.write_to !== 'alpha') {
    lines.push(`  .writeTo('${spec.write_to}')`);
  }
  lines.push(`  .execute();`);
  return lines.join('\n');
}

export default IngestForm;
